export function getParticipantLineItemGroups<LineItem extends ParticipantLineItem>(
  lineItems: readonly LineItem[],
) {
  const participantGroups = lineItems.reduce<LineItemGroup<LineItem>[]>(
    reduceLineItemsToParticipantGroups,
    [],
  );

  return [...participantGroups].sort(compareParticipantGroups);
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function reduceLineItemsToParticipantGroups<LineItem extends ParticipantLineItem>(
  groups: LineItemGroup<LineItem>[],
  lineItem: LineItem,
) {
  const participantId = lineItem.orderParticipant?.id ?? UNASSIGNED_PARTICIPANT_GROUP_ID;
  const existingGroup = groups.find((group) => group.id === participantId);

  if (!existingGroup) {
    return [...groups, createParticipantGroup({ lineItem, participantId })];
  }

  return groups.map((group) => addLineItemToParticipantGroup({ group, lineItem, participantId }));
}

function createParticipantGroup<LineItem extends ParticipantLineItem>(
  params: CreateParticipantGroupParams<LineItem>,
): LineItemGroup<LineItem> {
  const { lineItem, participantId } = params;
  const participant = lineItem.orderParticipant;

  return {
    id: participantId,
    displayName: participant?.displayName,
    isMe: participant?.isMe ?? false,
    lineItems: [lineItem],
  };
}

function addLineItemToParticipantGroup<LineItem extends ParticipantLineItem>(
  params: AddLineItemToParticipantGroupParams<LineItem>,
) {
  const { group, lineItem, participantId } = params;

  if (group.id !== participantId) return group;

  return { ...group, lineItems: [...group.lineItems, lineItem] };
}

function compareParticipantGroups<LineItem extends ParticipantLineItem>(
  currentGroup: LineItemGroup<LineItem>,
  nextGroup: LineItemGroup<LineItem>,
) {
  return Number(nextGroup.isMe) - Number(currentGroup.isMe);
}

// ─── Constants ───────────────────────────────────────────────────────────────

const UNASSIGNED_PARTICIPANT_GROUP_ID = 'unassigned-participant';

// ─── Types ───────────────────────────────────────────────────────────────────

type ParticipantLineItem = {
  orderParticipant?: {
    id: string;
    displayName?: string | null;
    isMe?: boolean;
  } | null;
};

type LineItemGroup<LineItem> = {
  id: string;
  displayName?: string | null;
  isMe: boolean;
  lineItems: readonly LineItem[];
};

type CreateParticipantGroupParams<LineItem> = {
  lineItem: LineItem;
  participantId: string;
};

type AddLineItemToParticipantGroupParams<LineItem> = {
  group: LineItemGroup<LineItem>;
  lineItem: LineItem;
  participantId: string;
};
